import { useEffect, useRef, useState, useCallback } from 'react';
import { WS_MESSAGES } from '../utils/constants';

const WS_URL = process.env.REACT_APP_WEBSOCKET_URL || 'ws://localhost:3001';

export const useWebSocket = (userId) => {
  const [connected, setConnected] = useState(false);
  const [gameState, setGameState] = useState(null);
  const [user, setUser] = useState(null);
  const [history, setHistory] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [winner, setWinner] = useState(null);
  
  const ws = useRef(null);
  const reconnectTimeout = useRef(null);
  const isConnecting = useRef(false);

  const handleMessage = useCallback((type, payload) => {
    switch (type) {
      case WS_MESSAGES.CONNECTED:
        setUser(payload.user);
        setGameState(payload.game);
        console.log('✅ Connected to game');
        break;

      case WS_MESSAGES.GAME_UPDATED:
        setGameState(payload.game);
        break;

      case WS_MESSAGES.GAME_STATE:
        setGameState(payload.game);
        break;

      case WS_MESSAGES.COUNTDOWN_UPDATE:
        setCountdown(payload.timeLeft);
        break;

      case WS_MESSAGES.GAME_START:
        setGameState(prev => prev ? { ...prev, status: 'spinning' } : null);
        setWinner(null);
        break;

      case WS_MESSAGES.GAME_RESULT:
        setGameState(payload.game);
        setWinner(payload.winner);
        break;

      case WS_MESSAGES.NEW_GAME:
        setGameState(payload.game);
        setWinner(null);
        setCountdown(null);
        break;

      case WS_MESSAGES.HISTORY:
        setHistory(payload.games);
        break;

      case WS_MESSAGES.INVENTORY:
        setInventory(payload.inventory);
        break;

      case WS_MESSAGES.ERROR:
        setError(payload.message);
        console.error('Server error:', payload.message);
        break;

      default:
        console.warn('Unknown message type:', type);
    }
  }, []);

  const send = useCallback((type, payload) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({ type, payload }));
    } else {
      console.error('WebSocket is not connected');
    }
  }, []);

  const connect = useCallback(() => {
    if (!userId || isConnecting.current || ws.current?.readyState === WebSocket.OPEN) {
      return;
    }

    isConnecting.current = true;
    console.log('🔌 Connecting to WebSocket...');
    
    try {
      ws.current = new WebSocket(WS_URL);

      ws.current.onopen = () => {
        console.log('✅ WebSocket connected');
        setConnected(true);
        setError(null);
        isConnecting.current = false;
        
        // Send connect message
        const connectMsg = {
          type: WS_MESSAGES.CONNECT,
          payload: {
            userId,
            telegramData: window.Telegram?.WebApp?.initDataUnsafe?.user || {}
          }
        };
        ws.current.send(JSON.stringify(connectMsg));
      };

      ws.current.onmessage = (event) => {
        try {
          const { type, payload } = JSON.parse(event.data);
          handleMessage(type, payload);
        } catch (err) {
          console.error('Error parsing message:', err);
        }
      };

      ws.current.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        setError('Connection error');
        isConnecting.current = false;
      };

      ws.current.onclose = () => {
        console.log('🔌 WebSocket disconnected');
        setConnected(false);
        isConnecting.current = false;
        
        // Clear existing timeout
        if (reconnectTimeout.current) {
          clearTimeout(reconnectTimeout.current);
        }
        
        // Attempt to reconnect after 3 seconds
        reconnectTimeout.current = setTimeout(() => {
          console.log('🔄 Attempting to reconnect...');
          connect();
        }, 3000);
      };
    } catch (err) {
      console.error('Failed to create WebSocket:', err);
      isConnecting.current = false;
    }
  }, [userId, handleMessage]);

  const placeGift = useCallback((giftId) => {
    send(WS_MESSAGES.PLACE_GIFT, { userId, giftId });
  }, [userId, send]);

  const getHistory = useCallback((limit = 20) => {
    send(WS_MESSAGES.GET_HISTORY, { limit });
  }, [send]);

  const getInventory = useCallback(() => {
    send(WS_MESSAGES.GET_INVENTORY, { userId });
  }, [userId, send]);

  const getGameState = useCallback(() => {
    send(WS_MESSAGES.GET_GAME_STATE, { userId });
  }, [userId, send]);

  useEffect(() => {
    if (userId) {
      connect();
    }

    return () => {
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }
      if (ws.current) {
        ws.current.close();
        ws.current = null;
      }
      isConnecting.current = false;
    };
  }, [userId, connect]);

  return {
    connected,
    gameState,
    user,
    history,
    inventory,
    error,
    countdown,
    winner,
    placeGift,
    getHistory,
    getInventory,
    getGameState
  };
};